/**
 * Panel controller — owns session, bootstrap, bridge, and chat stream.
 * App.tsx stays a thin view over this hook (Flutter ViewModel-style).
 */

import {
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { changeLocale, i18n } from '../i18n';
import { log } from '../config/env';
import {
  postToHost,
  readQueryParams,
  resolvePublishableKey,
} from '../lib/bridge';
import { isHostBridgeMessage } from '../lib/apiGuards';
import { useImageAttachments } from './useImageAttachments';
import {
  allowlistBlockReason,
  isIdentityVerificationEnabled,
  isLauncherEnabled,
  resolveFeatures,
  resolveHostOrigin,
  resolvePopularQuestions,
} from '../lib/configHelpers';
import {
  describeError,
  isAbortError,
  isNetworkFailure,
  isWidgetDisabledError,
} from '../lib/errors';
import { shouldAutoOpen, isTrustedHostMessage } from '../lib/hostTrust';
import { createId } from '../lib/ids';
import { streamAvatarReply } from '../lib/sseClient';
import { isRecord } from '../lib/typeGuards';
import { readVisitorId, writeVisitorId } from '../lib/visitorStorage';
import {
  ensureSession,
  fetchWidgetConfig,
  isSessionValid,
  type ActiveSession,
} from '../lib/widgetApi';
import {
  applyConfigAccent,
  clearConfigAccent,
  resolveConfigLocale,
} from '../theme';
import type { WidgetConfig, WidgetFeatures } from '../types/api';
import type { WidgetChannel } from '../types/bridge';
import type { ChatMessage, ImageAttachment } from '../types/chat';

function publicErrorKey(
  error?: unknown,
  reason?: string,
): 'chat.widgetDisabled' | 'chat.networkError' | 'chat.errorFallback' {
  if (isWidgetDisabledError(error, reason)) return 'chat.widgetDisabled';
  if (isNetworkFailure(error)) return 'chat.networkError';
  return 'chat.errorFallback';
}

export interface AvatarWidgetController {
  channel: WidgetChannel;
  isOpen: boolean;
  /** Config loaded or bootstrap failed — ready to show chat (not shimmer). */
  isPanelReady: boolean;
  messages: ChatMessage[];
  isStreaming: boolean;
  bootstrapping: boolean;
  serviceUnavailable: boolean;
  lastFailedText: string | null;
  widgetConfig: WidgetConfig | null;
  greetingText: string;
  features: WidgetFeatures;
  popularQuestions: string[];
  showPopular: boolean;
  inputDisabled: boolean;
  sendMessage: (text: string) => Promise<void>;
  /** Composer images (max MAX_IMAGE_ATTACHMENTS). Purged with the chat on close. */
  attachments: ImageAttachment[];
  addAttachments: (files: File[]) => void;
  removeAttachment: (id: string) => void;
  resetConversation: () => Promise<void>;
  /** Mobile chrome back — purges in-memory chat and asks the native host to dismiss. */
  dismissPanel: () => void;
}

export function useAvatarWidget(): AvatarWidgetController {
  const { t } = useTranslation();
  const { publishableKey, channel, bundleId } = readQueryParams();
  const autoOpen = shouldAutoOpen();

  const [isOpen, setIsOpen] = useState(autoOpen);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isStreaming, setIsStreaming] = useState(false);
  const [serviceUnavailable, setServiceUnavailable] = useState(false);
  const [lastFailedText, setLastFailedText] = useState<string | null>(null);
  const [widgetConfig, setWidgetConfig] = useState<WidgetConfig | null>(null);
  const [bootstrapping, setBootstrapping] = useState(false);
  const [isConfigReady, setIsConfigReady] = useState(false);
  const { attachments, addAttachments, removeAttachment, clearAttachments } =
    useImageAttachments();

  const abortRef = useRef<AbortController | null>(null);
  const bootstrapAbortRef = useRef<AbortController | null>(null);
  const configLoadRef = useRef<Promise<WidgetConfig> | null>(null);
  const pkRef = useRef(publishableKey);
  const channelRef = useRef<WidgetChannel>(channel);
  const bundleRef = useRef(bundleId);
  const sessionRef = useRef<ActiveSession | null>(null);
  const configRef = useRef<WidgetConfig | null>(null);
  const identityRef = useRef<{ userId: string; hmac: string } | null>(null);
  const conversationStartedRef = useRef(false);
  const isOpenRef = useRef(isOpen);
  const messagesRef = useRef<ChatMessage[]>([]);
  /** Set when Gateway says the widget was disabled mid-session. */
  const widgetDisabledRef = useRef(false);

  const greetingText = widgetConfig?.welcome_message?.trim() || '';
  const features = resolveFeatures(widgetConfig?.features);
  const popularQuestions = resolvePopularQuestions(
    widgetConfig?.popular_questions,
  );

  useEffect(() => {
    isOpenRef.current = isOpen;
  }, [isOpen]);

  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  useEffect(() => {
    configRef.current = widgetConfig;
  }, [widgetConfig]);

  const purgeConversation = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    bootstrapAbortRef.current?.abort();
    bootstrapAbortRef.current = null;
    configLoadRef.current = null;
    // Session JWT is in-memory only. visitor_id stays in localStorage (§8.2).
    sessionRef.current = null;
    identityRef.current = null;
    configRef.current = null;
    conversationStartedRef.current = false;
    widgetDisabledRef.current = false;
    clearConfigAccent();
    setWidgetConfig(null);
    setIsConfigReady(false);
    setMessages([]);
    setIsStreaming(false);
    setServiceUnavailable(false);
    setLastFailedText(null);
    setBootstrapping(false);
    clearAttachments();
  }, [clearAttachments]);

  const dismissPanel = useCallback(() => {
    purgeConversation();
    setIsOpen(false);
    postToHost('WIDGET_CLOSE');
  }, [purgeConversation]);

  const startFreshConversation = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    conversationStartedRef.current = false;
    setMessages([]);
    setIsStreaming(false);
    setServiceUnavailable(false);
    setLastFailedText(null);
    // Open clears the launcher unread badge (proposal §5.2).
    postToHost('UNREAD_CHANGED', { unreadCount: 0 });
  }, []);

  /** Clear the thread and mint a new session JWT (same visitor_id). */
  const resetConversation = useCallback(async () => {
    if (isStreaming || bootstrapping || !pkRef.current) return;

    startFreshConversation();

    const visitorId =
      sessionRef.current?.visitorId || readVisitorId(pkRef.current);
    const controller = new AbortController();
    abortRef.current = controller;
    setBootstrapping(true);

    try {
      const identityRequired = isIdentityVerificationEnabled(
        configRef.current?.identity_verification,
      );
      if (identityRequired && !identityRef.current) {
        setMessages([
          {
            id: createId('system'),
            text: t('chat.identifyRequired'),
            sender: 'system',
            status: 'error',
            createdAt: Date.now(),
          },
        ]);
        return;
      }

      const session = await ensureSession({
        publishableKey: pkRef.current,
        channel: channelRef.current,
        bundleId: bundleRef.current,
        visitorId,
        current: null,
        identity: identityRef.current,
        force: true,
        signal: controller.signal,
      });
      if (controller.signal.aborted) return;

      sessionRef.current = session;
      writeVisitorId(pkRef.current, session.visitorId);
      postToHost('WIDGET_THEME', {
        sessionId: session.sessionId,
        avatarId: session.avatarId,
      });
      log('info', 'Session refreshed after reset', {
        sessionId: session.sessionId,
        visitorId: session.visitorId,
      });
    } catch (error) {
      if (controller.signal.aborted) return;
      const reason = describeError(error, t('chat.errorFallback'));
      const publicText = t(publicErrorKey(error, reason));
      log('error', 'Session refresh after reset failed', reason);
      if (isWidgetDisabledError(error, reason)) {
        widgetDisabledRef.current = true;
      }
      setServiceUnavailable(true);
      setMessages([
        {
          id: createId('system'),
          text: publicText,
          sender: 'system',
          status: 'error',
          createdAt: Date.now(),
        },
      ]);
      postToHost('API_STREAM_ERROR', { reason });
    } finally {
      if (abortRef.current === controller) {
        abortRef.current = null;
        setBootstrapping(false);
      }
    }
  }, [bootstrapping, isStreaming, startFreshConversation, t]);

  /** GET /v1/config — deduped; applies accent before UI is shown. */
  const ensureConfigLoaded = useCallback(
    async (pk: string, signal?: AbortSignal): Promise<WidgetConfig> => {
      if (configRef.current) {
        applyConfigAccent(configRef.current.theme.accent_color);
        setIsConfigReady(true);
        return configRef.current;
      }

      if (configLoadRef.current) {
        return configLoadRef.current;
      }

      const load = (async () => {
        const config = await fetchWidgetConfig(pk, {
          signal,
          channel: channelRef.current,
          bundleId: bundleRef.current,
        });
        applyConfigAccent(config.theme.accent_color);
        setWidgetConfig(config);
        setIsConfigReady(true);
        return config;
      })();

      configLoadRef.current = load;
      try {
        return await load;
      } finally {
        if (configLoadRef.current === load) {
          configLoadRef.current = null;
        }
      }
    },
    [],
  );

  /**
   * Load avatar config + open a chat session.
   * Privacy: chat + session JWT live in memory only; purged on WIDGET_CLOSE.
   * visitor_id is persisted per pk on the panel origin (proposal §8.2).
   */
  const bootstrapWidget = useCallback(async (pk: string) => {
    if (!pk) {
      log('warn', 'bootstrap skipped — missing publishable key');
      return;
    }

    bootstrapAbortRef.current?.abort();
    const controller = new AbortController();
    bootstrapAbortRef.current = controller;
    const isCurrent = () => bootstrapAbortRef.current === controller;
    setBootstrapping(true);

    try {
      const config = await ensureConfigLoaded(pk, controller.signal);
      if (!isCurrent()) return;

      const hostOrigin = resolveHostOrigin();
      const blocked = allowlistBlockReason(config, {
        channel: channelRef.current,
        origin: hostOrigin,
        bundleId: bundleRef.current,
      });
      if (blocked) {
        log('warn', 'Embed is not on config allowlist (Gateway still decides)', {
          channel: channelRef.current,
          origin: hostOrigin,
          bundleId: bundleRef.current,
          allowlist: config.allowlist.origins,
          mobileBundles: config.allowlist.mobile_bundles,
          reason: blocked,
        });
      }

      const configLocale = resolveConfigLocale({
        localeDefault: config.locale_default,
        localesSupported: config.locales_supported,
      });
      if (configLocale) {
        await changeLocale(configLocale);
        if (!isCurrent()) return;
      }

      if (!isIdentityVerificationEnabled(config.identity_verification)) {
        identityRef.current = null;
      }

      const identityRequired = isIdentityVerificationEnabled(
        config.identity_verification,
      );
      if (identityRequired && !identityRef.current) {
        // Gateway rejects POST /v1/sessions without identity when enabled.
        // Wait for WIDGET_IDENTIFY (host calls identify before/after open).
        log('info', 'Identity verification enabled — waiting for identify()', {
          avatarId: config.avatar_id,
        });
        setServiceUnavailable(false);
        postToHost('WIDGET_THEME', {
          name: config.name,
          avatarId: config.avatar_id,
          accentColor: config.theme.accent_color,
          launcherIcon: config.theme.launcher_icon,
          position: config.theme.position,
          identityVerification: config.identity_verification,
          locale: config.locale_default,
          features: {
            fileUpload: config.features.file_upload,
            voice: config.features.voice,
            preChatForm: config.features.pre_chat_form,
          },
        });
        return;
      }

      if (!isLauncherEnabled(config)) {
        postToHost('WIDGET_THEME', {
          name: config.name,
          avatarId: config.avatar_id,
          accentColor: config.theme.accent_color,
          launcherIcon: config.theme.launcher_icon,
          position: config.theme.position,
          identityVerification: config.identity_verification,
          widgetStatus: false,
          locale: config.locale_default,
          features: {
            fileUpload: config.features.file_upload,
            voice: config.features.voice,
            preChatForm: config.features.pre_chat_form,
          },
        });
        setServiceUnavailable(false);
        log('info', 'Widget hidden — widget_status is false', {
          avatarId: config.avatar_id,
        });
        return;
      }

      const session = await ensureSession({
        publishableKey: pk,
        channel: channelRef.current,
        bundleId: bundleRef.current,
        visitorId: sessionRef.current?.visitorId || readVisitorId(pk),
        current: sessionRef.current,
        identity: identityRef.current,
        signal: controller.signal,
      });
      if (!isCurrent()) return;

      sessionRef.current = session;
      writeVisitorId(pk, session.visitorId);
      postToHost('WIDGET_THEME', {
        name: config.name,
        avatarId: config.avatar_id,
        accentColor: config.theme.accent_color,
        launcherIcon: config.theme.launcher_icon,
        position: config.theme.position,
        identityVerification: config.identity_verification,
        widgetStatus: config.widget_status,
        sessionId: session.sessionId,
        locale: config.locale_default,
        features: {
          fileUpload: config.features.file_upload,
          voice: config.features.voice,
          preChatForm: config.features.pre_chat_form,
        },
      });
      setServiceUnavailable(false);
      log('info', 'Widget bootstrapped', {
        avatarId: config.avatar_id,
        sessionId: session.sessionId,
        visitorId: session.visitorId,
        accent: config.theme.accent_color,
        locale: config.locale_default,
        localesSupported: config.locales_supported,
        identityVerification: config.identity_verification,
        widgetStatus: config.widget_status,
        hasIdentity: Boolean(identityRef.current),
        features: config.features,
        branding: config.branding,
        allowlistOrigins: config.allowlist.origins.length,
        mobileBundles: config.allowlist.mobile_bundles.length,
      });
    } catch (error) {
      if (!isCurrent()) return;
      if (controller.signal.aborted || isAbortError(error)) return;

      const reason = describeError(error, i18n.t('chat.errorFallback'));
      const publicText = i18n.t(publicErrorKey(error, reason));
      log('error', 'Widget bootstrap failed', reason);
      if (isWidgetDisabledError(error, reason)) {
        widgetDisabledRef.current = true;
      }
      setServiceUnavailable(true);
      setMessages([
        {
          id: createId('system'),
          text: publicText,
          sender: 'system',
          status: 'error',
          createdAt: Date.now(),
        },
      ]);
      postToHost('API_STREAM_ERROR', { reason });
    } finally {
      if (isCurrent()) setBootstrapping(false);
    }
  }, [ensureConfigLoaded]);

  /** Prefetch config on load — no AbortSignal (StrictMode cleanup must not cancel shared fetch). */
  useEffect(() => {
    if (!publishableKey) return;

    void ensureConfigLoaded(publishableKey).catch((error) => {
      if (isAbortError(error)) return;
      log('debug', 'Config prefetch failed (will retry on open)', error);
    });
  }, [publishableKey, ensureConfigLoaded]);

  useEffect(() => {
    if (!autoOpen || !pkRef.current) return;
    void bootstrapWidget(pkRef.current);
  }, [autoOpen, bootstrapWidget]);

  useEffect(() => {
    const handleBridgeMessages = (event: MessageEvent) => {
      if (!isHostBridgeMessage(event.data)) return;
      if (!isTrustedHostMessage(event.origin, configRef.current)) return;

      if (event.data.event === 'WIDGET_OPEN') {
        const payload = isRecord(event.data.payload) ? event.data.payload : {};
        const key = resolvePublishableKey({
          publishableKey:
            typeof payload.publishableKey === 'string'
              ? payload.publishableKey
              : undefined,
          publicKey:
            typeof payload.publicKey === 'string'
              ? payload.publicKey
              : undefined,
        });
        if (key) pkRef.current = key;
        setIsOpen(true);
        startFreshConversation();
        void bootstrapWidget(pkRef.current);
      }

      if (event.data.event === 'WIDGET_CLOSE') {
        if (widgetDisabledRef.current) {
          postToHost('WIDGET_THEME', { widgetStatus: false });
        }
        setIsOpen(false);
        // PRIVACY: purge chat + JWT. visitor_id remains in localStorage (§8.2).
        purgeConversation();
      }

      if (event.data.event === 'WIDGET_IDENTIFY') {
        const payload = isRecord(event.data.payload) ? event.data.payload : {};
        const userId =
          typeof payload.userId === 'string'
            ? payload.userId
            : typeof payload.user_id === 'string'
              ? payload.user_id
              : undefined;
        const hmac =
          typeof payload.hmac === 'string'
            ? payload.hmac
            : typeof payload.userHash === 'string'
              ? payload.userHash
              : typeof payload.user_hash === 'string'
                ? payload.user_hash
                : undefined;
        if (!userId || !hmac) return;

        const verification = configRef.current?.identity_verification;
        if (
          verification !== undefined &&
          !isIdentityVerificationEnabled(verification)
        ) {
          log('warn', i18n.t('chat.identifyDisabled'));
          return;
        }
        identityRef.current = { userId, hmac };
        log('info', 'Identity payload stored — minting session with HMAC', {
          userId,
        });

        // Bind mid-session or complete bootstrap that waited for identify.
        if (pkRef.current) {
          void (async () => {
            try {
              const session = await ensureSession({
                publishableKey: pkRef.current,
                channel: channelRef.current,
                bundleId: bundleRef.current,
                visitorId:
                  sessionRef.current?.visitorId ||
                  readVisitorId(pkRef.current),
                current: sessionRef.current,
                identity: identityRef.current,
                force: true,
              });
              sessionRef.current = session;
              writeVisitorId(pkRef.current, session.visitorId);
              setServiceUnavailable(false);
              postToHost('WIDGET_THEME', {
                sessionId: session.sessionId,
                identityVerification:
                  configRef.current?.identity_verification,
              });
            } catch (error) {
              const reason = describeError(
                error,
                i18n.t('chat.errorFallback'),
              );
              log('error', 'Session after identify failed', reason);
              setServiceUnavailable(true);
              postToHost('API_STREAM_ERROR', { reason });
            }
          })();
        }
      }
    };

    window.addEventListener('message', handleBridgeMessages);
    postToHost('WIDGET_READY');

    return () => {
      window.removeEventListener('message', handleBridgeMessages);
      abortRef.current?.abort();
    };
  }, [bootstrapWidget, purgeConversation, startFreshConversation]);

  const sendMessage = useCallback(
    async (text: string) => {
      if (serviceUnavailable || isStreaming || bootstrapping) return;

      const sentAt = Date.now();
      const userMsg: ChatMessage = {
        id: createId('user'),
        text,
        sender: 'user',
        status: 'complete',
        createdAt: sentAt,
      };
      const streamId = createId('avatar');
      const placeholder: ChatMessage = {
        id: streamId,
        text: '',
        sender: 'avatar',
        status: 'streaming',
        createdAt: sentAt,
      };

      const previousSession = sessionRef.current;
      const sessionHadEnded = Boolean(
        previousSession && !isSessionValid(previousSession),
      );
      const hadTranscript = messagesRef.current.some(
        (m) => m.sender === 'user' || m.sender === 'avatar',
      );

      setMessages((prev) => [...prev, userMsg, placeholder]);
      // TODO: upload with the message once the Gateway upload API exists.
      // Until then images are dropped on send, not transmitted.
      clearAttachments();
      setIsStreaming(true);
      setLastFailedText(null);

      const controller = new AbortController();
      abortRef.current = controller;

      const failStream = (reason: string, error?: unknown) => {
        const disabled = isWidgetDisabledError(error, reason);
        const publicText = t(publicErrorKey(error, reason));
        setMessages((prev) =>
          prev.map((m) =>
            m.id === streamId
              ? { ...m, sender: 'system', status: 'error', text: publicText }
              : m,
          ),
        );
        setLastFailedText(disabled ? null : text);
        setIsStreaming(false);
        if (disabled) {
          widgetDisabledRef.current = true;
          setServiceUnavailable(true);
        }
        postToHost('API_STREAM_ERROR', { reason });
      };

      try {
        const identityRequired = isIdentityVerificationEnabled(
          configRef.current?.identity_verification,
        );
        if (identityRequired && !identityRef.current) {
          failStream(t('chat.identifyRequired'));
          return;
        }

        const session = await ensureSession({
          publishableKey: pkRef.current,
          channel: channelRef.current,
          bundleId: bundleRef.current,
          visitorId:
            sessionRef.current?.visitorId || readVisitorId(pkRef.current),
          current: sessionRef.current,
          identity: identityRef.current,
          signal: controller.signal,
        });
        sessionRef.current = session;
        writeVisitorId(pkRef.current, session.visitorId);

        if (hadTranscript && sessionHadEnded) {
          conversationStartedRef.current = false;
          setMessages((prev) => {
            const insertAt = prev.findIndex((m) => m.id === userMsg.id);
            if (insertAt < 0) return prev;
            if (prev[insertAt - 1]?.sender === 'divider') return prev;
            const divider: ChatMessage = {
              id: createId('divider'),
              text: t('chat.newConversationStarted'),
              sender: 'divider',
              createdAt: Date.now(),
            };
            return [
              ...prev.slice(0, insertAt),
              divider,
              ...prev.slice(insertAt),
            ];
          });
        }

        let activeConversationId: string | undefined;

        await streamAvatarReply({
          accessToken: session.accessToken,
          message: text,
          channel: channelRef.current,
          bundleId: bundleRef.current,
          signal: controller.signal,
          onChunk: (chunk) => {
            if (chunk.type === 'started') {
              if (chunk.conversationId) {
                activeConversationId = chunk.conversationId;
              }
              if (activeConversationId && !conversationStartedRef.current) {
                conversationStartedRef.current = true;
                postToHost('CONVERSATION_STARTED', {
                  conversationId: activeConversationId,
                  sessionId: chunk.sessionId || session.sessionId,
                  avatarId: chunk.avatarId || session.avatarId,
                  publishableKey: pkRef.current,
                });
              }
            }

            if (chunk.type === 'token' && chunk.text) {
              setMessages((prev) =>
                prev.map((m) =>
                  m.id === streamId ? { ...m, text: m.text + chunk.text } : m,
                ),
              );
            }

            if (chunk.type === 'done') {
              const completedText = chunk.text;
              setMessages((prev) => {
                const next = prev.map((m) =>
                  m.id === streamId
                    ? {
                        ...m,
                        text: m.text || completedText || '',
                        status: 'complete' as const,
                      }
                    : m,
                );
                const finalText =
                  next.find((m) => m.id === streamId)?.text || '';
                postToHost('MESSAGE_RECEIVED', {
                  conversationId: chunk.conversationId || activeConversationId,
                  sessionId: chunk.sessionId || session.sessionId,
                  avatarId: chunk.avatarId || session.avatarId,
                  publishableKey: pkRef.current,
                  role: 'assistant',
                  messagePreview: finalText.slice(0, 160),
                });
                if (!isOpenRef.current) {
                  postToHost('UNREAD_CHANGED', { unreadCount: 1 });
                }
                return next;
              });
              setIsStreaming(false);
            }

            if (chunk.type === 'error') {
              failStream(chunk.reason || t('chat.errorFallback'));
            }
          },
        });
      } catch (error) {
        if (controller.signal.aborted) return;
        failStream(describeError(error, t('chat.errorFallback')), error);
      }
    },
    [bootstrapping, clearAttachments, isStreaming, serviceUnavailable, t],
  );

  const showPopular =
    messages.length === 0 &&
    !isStreaming &&
    !serviceUnavailable &&
    (greetingText.length > 0 || popularQuestions.length > 0);
  const inputDisabled = serviceUnavailable || isStreaming || bootstrapping;
  const isPanelReady = isConfigReady || serviceUnavailable;

  return {
    channel,
    isOpen,
    isPanelReady,
    messages,
    isStreaming,
    bootstrapping,
    serviceUnavailable,
    lastFailedText,
    widgetConfig,
    greetingText,
    features,
    popularQuestions,
    showPopular,
    inputDisabled,
    sendMessage,
    attachments,
    addAttachments,
    removeAttachment,
    resetConversation,
    dismissPanel,
  };
}
